// /services/custom-3d-printing/ (PrintingProcessPage.js). Code-owned since
// 2026-11-04; gatsby-node skips the Contentful service entry.
// GSC Aug–Oct 2026: 1 click, 632 impressions at position ~18. The homepage
// ranks #1 for "custom 3d printing service" and /services/3d-printing/ owns
// "online 3d printing service", so this page is the finished-pieces variant:
// "custom 3d printing" (3,600/mo) with paint, plating and assembly.
// Facts (truth.md): projects can start from a sketch, image or file and can
// include modeling, printing, assembly, painting or metal finishing when
// quoted; no process is universally best; 24-hour MJF isn't promised (MJF and
// SLS are left out). Prices: printing from about $30, modeling and scanning
// from $250; finishing quoted per project.
const {
  pricing,
  photoRoot,
  reviewedDate,
  studios,
  delivery,
  files,
  noFile,
  hobby,
  quoteSteps,
  handoffStep,
} = require('./shared')
const printing = require('../printing-service')
const printingNyc = require('../printing-nyc')

const page = {
  path: '/services/custom-3d-printing/',
  crumb: 'Custom 3D printing',
  title: 'Custom 3D Printing Service | Painted & Finished Prints',
  description: `Custom 3D printing for brands, agencies and studios: prototypes, sculptures, props and models, sanded, painted or metal plated. From about $${pricing.printFrom}, no setup fee.`,
  heading: 'Custom 3D Printing Service',
  eyebrow: 'Painted & finished 3D prints · From a file, sketch or photo',
  answer: `PrintAWorld is a custom 3D printing service in New York for finished pieces: prototypes, sculptures, props, display models and parts that arrive sanded, painted or metal plated, not just raw prints. Start from a 3D file, a sketch or a photo; we model it if needed, print it in-house on our resin, FDM and metal printers, and finish and assemble it. From one piece to a production run, with no setup fee: projects start around $${pricing.printFrom}, and we ship to all 50 states.`,
  hero: 'hands',
  faqHeading: 'What do people ask about custom 3D printing?',
  quoteText:
    'Send the file or photos, the size, the finish you want and the deadline. We’ll reply with a price and next steps.',
  reviewedDate,
}

const card = {
  label: 'Finished pieces',
  title: 'Custom 3D printing',
  body: 'Sanded, painted or plated prints for displays, props and gifts.',
}

const photos = {
  hands: {
    ...printingNyc.photos.hands,
  },
  jaguar: {
    url: `${photoRoot}/5IZLDukl7A3WXk8MEk3oiv/d59ed948665fb0604ce7e4a9a7a01c8d/LAL_4995_2.jpg`,
    width: 1350,
    height: 901,
    alt: 'A 3D printed animal model in clear resin showing white bones inside',
    title: 'Clear and white anatomy model',
    caption: 'An animal model printed in clear and white resin.',
  },
  clocks: {
    ...printing.photos.clocks,
  },
  chrome: {
    ...printing.photos.chrome,
  },
  creature: {
    ...printing.photos.creature,
    title: 'Glossy red creature',
    caption: 'A sculpture finished in a deep gloss red.',
  },
  hex: {
    url: `${photoRoot}/3Uvr953WnUhzbwhAbJAhT6/1b8efc0ed6bb2c47ac891003e2e0b64d/two_hands_holding_a_3d_printed_abs_plastic.png`,
    width: 6520,
    height: 4437,
    alt: 'Two hands holding white and black 3D printed hexagonal ABS containers',
    title: 'Product samples in two colors',
    caption: 'The same part printed in white and black to compare.',
  },
  carFrame: {
    ...printing.photos.carFrame,
  },
}

const intro = {
  eyebrow: 'More than a raw print',
  heading: 'What is custom 3D printing?',
  paragraphs: [
    'Custom 3D printing is a print made for one job: your shape, your size, your finish. A raw print comes off the machine with supports and layer lines; a custom piece is cleaned up, sanded, primed and painted or plated until it’s ready for a client, a shelf or a stage.',
    [
      'We do the whole job in our New York studios, for brands, agencies, artists and product teams. If you only need a print from a file, see our ',
      { to: '/services/3d-printing/', label: 'online 3D printing service' },
      '.',
    ],
  ],
}

const glance = {
  heading: 'Our custom 3D printing service at a glance',
  rows: [
    ['Studios', studios],
    ['Delivery', delivery],
    ['Production', 'In-house, in our New York studios'],
    [
      'Processes',
      'An FDM print farm, SLA resin, large-format and stainless-steel metal (SLM) 3D printing',
    ],
    [
      'Finishes',
      'As printed, sanded and primed, painted (one color, brand colors or full color), metal plated, clear',
    ],
    ['Assembly', 'Multi-part pieces joined, filled and finished as one'],
    ['Order size', 'One piece to production runs'],
    ['Setup fee', 'None for 3D printing'],
    [
      'Price',
      `From about $${pricing.printFrom} for a simple print; finishing quoted per project`,
    ],
    ['Files', files],
    ['No file?', noFile],
  ],
}

const work = {
  heading: 'Custom 3D printing projects from our studio.',
  photos: ['chrome', 'clocks', 'jaguar', 'creature', 'hex', 'carFrame'],
}

const before = [
  {
    id: 'finishes',
    eyebrow: 'Finishes',
    heading: 'Which finishes can you add to a custom 3D print?',
    index: '02 / THE FINISH',
    cards: [
      {
        label: 'F1',
        title: 'As printed',
        body: 'Supports removed; layer lines may show. Fine for fit checks and parts nobody sees.',
      },
      {
        label: 'F2',
        title: 'Sanded and primed',
        body: 'Smooth and ready for your own paint, or for a client to see the form.',
      },
      {
        label: 'F3',
        title: 'Painted',
        body: [
          'One color, brand colors or full color, by hand. See ',
          {
            to: '/full-color-3d-printing-service/',
            label: 'full-color 3D printing',
          },
          '.',
        ],
      },
      {
        label: 'F4',
        title: 'Metal plated',
        body: 'Chrome, gold or bronze over resin. Plating coats the surface; the piece isn’t solid metal.',
      },
    ],
    note: [
      'Need solid metal instead of a metal look? See ',
      { to: '/services/metal-3d-printing/', label: 'metal 3D printing' },
      ' in stainless steel.',
    ],
  },
]

const table = {
  id: 'processes',
  eyebrow: 'Processes',
  heading: 'Which 3D printing process fits your custom part?',
  index: '03 / THE PROCESS',
  intro:
    'No process is best for everything. We pick one for the size, detail, strength and finish your piece needs, and the quote names it.',
  columns: ['Process', 'Best for', 'Size', 'Learn more'],
  rows: [
    [
      'SLA resin',
      'Fine detail and smooth surfaces to paint or plate',
      'Up to a 600 × 600 mm platform, larger in sections',
      [{ to: '/services/sla-3d-printing/', label: 'SLA 3D printing' }],
    ],
    [
      'FDM plastic',
      'Tough working parts and production runs on our print farm',
      'Large parts and big runs',
      [{ to: '/services/3d-printing/', label: 'Online 3D printing' }],
    ],
    [
      'Large-format',
      'Sculptures, props and models of 6 feet and more',
      'Printed in sections and joined',
      [
        {
          to: '/large-scale-3d-printing-service/',
          label: 'Large-scale 3D printing',
        },
      ],
    ],
    [
      'Stainless steel (SLM)',
      'Solid metal parts and complex metal shapes',
      '250 × 250 × 300 mm',
      [{ to: '/services/metal-3d-printing/', label: 'Metal 3D printing' }],
    ],
  ],
  note: [
    'Choosing a material? Read the ',
    { to: '/materials/', label: '3D printing materials guide' },
    '.',
  ],
}

const contrast = {
  id: 'no-file',
  eyebrow: 'No 3D file?',
  heading: 'Can you make a custom 3D print without a 3D model?',
  paragraphs: [
    'Yes. Send a sketch, photos from a few angles or measurements. We model it, send it for your approval, then print and finish it.',
    `3D modeling starts at $${pricing.modelingFrom}. To copy a real object, 3D scanning starts at $${pricing.scanningFrom}.`,
  ],
  links: [
    { to: '/services/3d-modeling/', label: 'Explore 3D modeling' },
    { to: '/services/3d-print-from-photo/', label: '3D print from a photo' },
  ],
}

const fit = {
  eyebrow: 'Is it a fit?',
  heading: 'Is custom 3D printing right for your project?',
  index: '04 / THE FIT',
  good: [
    'Brands, agencies and businesses that need a finished piece, not a raw part',
    'Display pieces, props, sculptures and presentation models',
    'Product prototypes painted to look like the real thing',
    'Production runs of the same finished part, with no setup fee',
    'Projects that start from a sketch or a photo',
  ],
  other: [
    'Raw engineering parts at the lowest price per part: instant-quote websites compete on that.',
    'Very high volumes of a simple plastic part: injection molding usually costs less per piece.',
    hobby,
  ],
}

const pricingBlock = {
  heading: 'How much does custom 3D printing cost?',
  index: '05 / THE PRICE',
  lead: {
    label: '3D printing starts around',
    value: `$${pricing.printFrom}`,
    note: 'For a simple print from a file that’s ready to print.',
  },
  rows: [
    ['Simple print from your file', `From about $${pricing.printFrom}`],
    ['3D modeling, if there’s no file', `From $${pricing.modelingFrom}`],
    ['3D scanning, to copy an object', `From $${pricing.scanningFrom}`],
    ['Sanding, painting, plating', 'Quoted per project'],
    ['Assembly of multi-part pieces', 'Quoted per project'],
    ['Setup fee', 'None'],
    ['Shipping, next-day available', 'Quoted to your ZIP'],
  ],
  note: 'For planning only. Size, material, machine time, quantity and finishing set the final price; a print-ready file avoids modeling fees.',
}

const steps = {
  heading: 'How our custom 3D printing service works',
  index: '06 / THE PROCESS',
  items: [
    ...quoteSteps,
    {
      title: 'Model and approve',
      body: 'If there’s no file, we model it and send it for approval before anything is printed.',
    },
    {
      title: 'Print and finish',
      body: 'We print in our New York studios, then sand, paint, plate and assemble, as quoted.',
    },
    handoffStep,
  ],
}

const checklist = [
  'Your 3D file (STL, OBJ, STEP, STP or 3DM), or photos and a sketch',
  'Finished size, or the scale to print at',
  'How many you need',
  'The finish: raw, primed, painted, plated',
  'What it’s for: display, handling, a shoot, a gift',
  'Your delivery ZIP code and deadline',
]

const faqs = [
  {
    question: 'How much does custom 3D printing cost?',
    answer: `3D printing at PrintAWorld starts around $${pricing.printFrom} for a simple print from your file and goes up to thousands of dollars for large or finished pieces. Painting, plating and assembly are quoted per project. 3D modeling starts at $${pricing.modelingFrom} if you don’t have a file, and there’s no setup fee.`,
  },
  {
    question: 'Is custom 3D printing right for me?',
    answer:
      'It is if you need a finished piece: painted, plated or assembled, ready for a client, a display or a production run. For a cheap hobby print or parts to resell, a marketplace or a hobbyist will usually cost less.',
  },
  {
    question: 'How do I make a custom design for 3D printing?',
    answer:
      'Three ways: model it yourself in CAD (Fusion 360, SolidWorks, Tinkercad) or sculpting software (Blender, ZBrush); have it modeled, which we do from $250 with print-ready files; or start from an existing model you have the rights to print.',
  },
  {
    question: 'Can you paint 3D prints?',
    answer:
      'Yes. We sand, prime and paint in one color, brand colors or full color, by hand in our studios. Painting is quoted with the print.',
  },
  {
    question: 'Can you make a 3D print look like metal?',
    answer:
      'Yes, with metal plating: chrome, gold or bronze over a resin print. Plating coats the surface; the piece isn’t solid metal. For solid metal, we 3D print in stainless steel.',
  },
  {
    question: 'Is there a minimum order?',
    answer: `No. One piece is fine, and so is a production run on our FDM print farm. There’s no setup fee, and small jobs start around $${pricing.printFrom}.`,
  },
  {
    question: 'How long does custom 3D printing take?',
    answer:
      'Many prints can be made next day, and in New York we can deliver by courier. Painting, plating and assembly add time. Your written quote confirms the date; send your deadline with the file.',
  },
  {
    question: 'Do you ship custom 3D prints?',
    answer:
      'Yes, to all 50 states, with next-day shipping available once your piece is ready. You can also pick up by appointment in Glendale, Queens or Brooklyn.',
  },
]

module.exports = {
  page,
  card,
  photos,
  intro,
  glance,
  work,
  before,
  table,
  contrast,
  fit,
  pricing: pricingBlock,
  steps,
  checklist,
  faqs,
  schema: {
    name: 'Custom 3D Printing Service',
    serviceType: 'Custom 3D printing and finishing',
    description:
      'Custom 3D printing in New York for prototypes, sculptures, props and display models, with in-house modeling, printing, sanding, painting, metal plating and assembly, shipped to all 50 states.',
    offer:
      '3D printing from about $30 for a simple print from a ready file; finishing quoted per project.',
    minPrice: pricing.printFrom,
  },
}
